import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
  GridRowParams,
} from "@mui/x-data-grid-premium";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useTenantTranslation } from "hooks/formatters";
import { Serialiser } from "hooks/serialisers/types";
import { useTenant } from "hooks/settings";
import { useQueryParamState } from "hooks/useQueryParamState";
import { SyntheticEvent, useMemo, useState } from "react";
import { useListCompletedHeatsQuery } from "src/store/api/generatedApi";
import {
  COMPLETED_START_TIME,
  HEAT_GRID_SX,
  HeatCell,
  HeatRow,
  toHeatRow,
  useHeatDataColumns,
} from "../heatColumns";
import { OverrideRecipeModal } from "./OverrideRecipeModal";
import { useContiguousHeatSelection } from "./useContiguousHeatSelection";
import { usePreloadOverrideRecipeModal } from "./usePreloadOverrideRecipeModal";

const OVERRIDDEN_ROW_CLASS = "add-missing-overridden-row";

const heatCell: HeatCell<HeatRow> =
  (render) =>
  ({ row }) =>
    render(row);

const parseHeatId = (input: string): number | null =>
  /^\d+$/.test(input.trim()) ? Number(input.trim()) : null;

const heatIdSerialiser: Serialiser<number | null, unknown> = {
  format: (heatId) => heatId?.toString() ?? "",
  parse: (text) => ({ valid: true, value: parseHeatId(text) }),
};

export const AddMissingOverridesPage = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const [searchedHeatId, setSearchedHeatId] = useQueryParamState(
    "heatId",
    heatIdSerialiser,
    null
  );
  const [heatIdInput, setHeatIdInput] = useState(
    () => searchedHeatId?.toString() ?? ""
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { currentData, isFetching, error } = useListCompletedHeatsQuery(
    searchedHeatId === null ? skipToken : { tenantName, heatId: searchedHeatId }
  );
  const heats = useMemo(() => currentData?.heats ?? [], [currentData]);
  const {
    selectedHeats,
    selectableIds,
    toggleHeat,
    handleSelectionModelChange,
    clearSelection,
  } = useContiguousHeatSelection(heats);
  const rows = useMemo(
    () =>
      heats.map(toHeatRow).map((row) =>
        row.overrideId === null || row.recipeName === null
          ? row
          : {
              ...row,
              recipeName: t("overriddenRecipeName", { name: row.recipeName }),
            }
      ),
    [heats, t]
  );

  usePreloadOverrideRecipeModal({ tenantName, enabled: true });

  const dataColumns = useHeatDataColumns<HeatRow>({
    heatCell,
    startTime: COMPLETED_START_TIME,
  });
  const columns = useMemo(
    (): GridColDef<HeatRow>[] => [
      { ...GRID_CHECKBOX_SELECTION_COL_DEF, renderHeader: () => null },
      ...dataColumns,
    ],
    [dataColumns]
  );

  const firstSelectedHeat = selectedHeats[0];

  const search = (event: SyntheticEvent) => {
    event.preventDefault();
    clearSelection();
    setSearchedHeatId(parseHeatId(heatIdInput));
  };

  return (
    <AccountPageLayout title={t("addMissingOverrides")}>
      <Stack sx={{ gap: 2 }}>
        <Stack
          component="form"
          direction="row"
          onSubmit={search}
          sx={{ gap: 2, alignItems: "center" }}
        >
          <TextField
            label={t("completedHeatId")}
            value={heatIdInput}
            onChange={(event) => {
              setHeatIdInput(event.target.value);
            }}
            size="small"
            slotProps={{ htmlInput: { inputMode: "numeric" } }}
          />
          <LoadingButton
            type="submit"
            variant="outlined"
            loading={isFetching}
            disabled={parseHeatId(heatIdInput) === null}
          >
            {t("search")}
          </LoadingButton>
        </Stack>
        {error !== undefined ? (
          <Alert severity="error">{t("completedHeatsLoadError")}</Alert>
        ) : currentData !== undefined && heats.length === 0 ? (
          <Alert severity="info">
            {t("noCompletedHeatWithId", { heatId: searchedHeatId })}
          </Alert>
        ) : (
          heats.length > 0 && (
            <Box>
              <Stack
                direction="row"
                sx={{ alignItems: "center", gap: 2, mb: 2, minHeight: 36 }}
              >
                <Button
                  variant="contained"
                  disabled={selectedHeats.length === 0}
                  onClick={() => {
                    setIsModalOpen(true);
                  }}
                >
                  {t("editRecipe")}
                </Button>
                {selectedHeats.length > 0 && (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t("heatsSelected", { count: selectedHeats.length })}
                  </Typography>
                )}
              </Stack>
              <HotkeyCompliantDataGrid
                columns={columns}
                rows={rows}
                loading={isFetching}
                autoHeight
                disableColumnSorting
                checkboxSelection
                keepNonExistentRowsSelected
                disableRowSelectionOnClick
                isRowSelectable={({ row }) =>
                  selectableIds.has(row.stableHeatId)
                }
                rowSelectionModel={{
                  type: "include",
                  ids: new Set(selectedHeats.map((heat) => heat.heat_id)),
                }}
                onRowSelectionModelChange={handleSelectionModelChange}
                onRowClick={({ row }: GridRowParams<HeatRow>) => {
                  if (selectableIds.has(row.stableHeatId)) {
                    toggleHeat(row.stableHeatId);
                  }
                }}
                rowHeight={40}
                columnHeaderHeight={44}
                getRowClassName={({ row }) =>
                  row.overrideId === null ? "" : OVERRIDDEN_ROW_CLASS
                }
                slotProps={{
                  loadingOverlay: {
                    variant: "skeleton",
                    noRowsVariant: "skeleton",
                  },
                }}
                sx={{
                  ...HEAT_GRID_SX,
                  [`& .MuiDataGrid-row.${OVERRIDDEN_ROW_CLASS}, & .MuiDataGrid-row.${OVERRIDDEN_ROW_CLASS}:hover`]:
                    {
                      bgcolor: "action.disabledBackground",
                      "& .MuiTypography-root": { color: "text.disabled" },
                    },
                }}
              />
            </Box>
          )
        )}
      </Stack>
      {(firstSelectedHeat !== undefined || isModalOpen) && (
        <OverrideRecipeModal
          open={isModalOpen}
          doClose={() => {
            setIsModalOpen(false);
          }}
          onOverrideCreated={() => {
            clearSelection();
            setIsModalOpen(false);
          }}
          selectedHeats={selectedHeats}
          steelGrade={firstSelectedHeat?.steel_grade ?? ""}
          floorRecipe={firstSelectedHeat?.floor_recipe ?? null}
          heatTiming="completed"
        />
      )}
    </AccountPageLayout>
  );
};
